import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { APP, getFileChipBadgeColor } from './tokens';
import ExportBar from './ExportBar';
import type { ExportDestination } from './ExportBar';
import type { ChatMessage, EsignChatSummary, WorkflowThreadSummary } from '../../types';
import type { CoTState } from '../../hooks/useCoTState';
import type { UseQueryRunStateResult } from '../../hooks/useQueryRunState';
import type { DocEditRecoveryResult, DocumentVersionSummary, EditBatch, HtmlEditApplyResult } from '@corner/shared';
import { CoTContainer } from './cot';
import CoTEditBatchCard from './cot/CoTEditBatchCard';
import QueryRunPanel from './QueryRunPanel';
import CommentThreadList from './CommentThreadList';
import { authedDownload, downloadUrlWithAuthIfNeeded, appendTokenToUrl } from '../../lib/authedDownload';
import { SnippetChip, type SelectedSnippetChip } from './InputBar';
import RiskReviewEntryBanner from './clauseReview/RiskReviewEntryBanner';

// ─── Types ──────────────────────────────────────

export interface ActionChip {
  label: string;
  variant: 'initial' | 'followup';
  /** When set, send this text instead of the label when the chip is clicked. */
  sendAs?: string;
}

export interface AnalysisResultData {
  /** Bold title line, e.g. "Annual Report Notice — Summary" */
  title: string;
  /** Body text */
  body: string;
  /** Action chips shown inside the bubble */
  chips: ActionChip[];
  /** When true, show the EXPORT TO bar with all 6 destinations */
  exportable?: boolean;
  /** Optional risk summary from a completed clause review (shown as a badge on contract_review) */
  riskSummary?: {
    hasHighRisk: boolean;
    highRiskCount: number;
    mediumRiskCount: number;
    label: string;
    nudgeLevel: 'silent' | 'attention' | 'reconsider';
    lane: string;
    jurisdiction: string | null;
    clauseReviewId: string;
  } | null;
}

export interface MergeResultData {
  /** e.g. "Merged in 1.8s" */
  headerTitle: string;
  /** Each source file with name + page count */
  files: { name: string; pageCount: number }[];
  /** Download action */
  onDownload: () => void;
  /** e.g. "Merged_Document.pdf · 14 pages · 4.1 MB" */
  fileMetaLine: string;
}

export interface SplitResultData {
  /** e.g. "Split in 0.9s — 5 files" */
  headerTitle: string;
  /** Individual page files with download URLs */
  files: { name: string; downloadUrl: string }[];
  /** Download all as ZIP */
  onDownloadZip: () => void;
  /** For per-page downloads from `/api/file/...` */
  getToken?: () => string | null;
}

export interface OcrResultData {
  /** e.g. "OCR complete · 98% confidence" */
  headerTitle: string;
  /** Extracted text preview (truncated for display) */
  textPreview: string;
  /** Download .txt file */
  onDownload: () => void;
  /** Copy text to clipboard */
  onCopy: () => void;
  /** Footer line, e.g. "142 words · extracted in 1.2s" */
  footerLine: string;
}

export interface QrResultData {
  /** e.g. "QR code generated" */
  headerTitle: string;
  /** e.g. "corner.app · PNG 512×512px" */
  subtitle: string;
  /** Download current format (PNG or SVG) */
  onDownloadPrimary: () => void;
  /** Label for primary download button, e.g. "Download PNG" */
  primaryLabel: string;
  /** Download alternate format */
  onDownloadAlternate: () => void;
  /** Label for alternate button, e.g. "SVG" */
  alternateLabel: string;
}

export interface WatermarkResultData {
  /** e.g. "Watermark applied to all 5 pages" */
  headerTitle: string;
  /** The watermark text, e.g. "CONFIDENTIAL" */
  watermarkText: string;
  /** Download watermarked PDF */
  onDownload: () => void;
  /** Footer line, e.g. "Annual_Report_2024_watermarked.pdf · 2.1 MB" */
  footerLine: string;
}

export interface PdfUtilityResultData {
  /** e.g. "Page numbers added · 5 pages" */
  headerTitle: string;
  /** Optional detail row label, e.g. "Position:" */
  detailLabel?: string;
  /** Optional detail row value, e.g. "Bottom center" */
  detailValue?: string;
  /** Download action */
  onDownload: () => void;
  /** Download button label, e.g. "Download updated PDF" */
  downloadLabel: string;
  /** Footer line, e.g. "Annual_Report_2024_numbered.pdf · 1.8 MB" */
  footerLine: string;
}

export interface ToolResultCardData {
  /** Success header text, e.g. "PDF Protected", "Pages Rotated" */
  headerTitle: string;
  /** Icon key — drives the inline SVG in the card body */
  icon: 'lock' | 'rotate' | 'repair';
  /** Main description text, e.g. "Password protection applied to PDF" */
  description: string;
  /** Optional file metadata line, e.g. { pages: "5 pages", size: "1.2 MB" } */
  fileMeta?: { pages: string; size: string };
  /** Optional list of detail items shown in a box (e.g. repair issues) */
  details?: string[];
  /** Download action */
  onDownload: () => void;
}

export interface CitationResultData {
  /** Success header text, e.g. "Citation Generated" */
  headerTitle: string;
  /** Currently active format label, e.g. "APA Format" */
  formatLabel: string;
  /** The citation text */
  citationText: string;
  /** Copy to clipboard action */
  onCopy: () => void;
  /** Available format options; the active one has `active: true` */
  formats: { label: string; active?: boolean; onSelect: () => void }[];
}

export interface IntegrationIssueTag {
  label: string;
  /** When true, uses the provider accent color (e.g. Linear indigo) */
  highlighted?: boolean;
  /** Optional status dot color; omit for no dot */
  dotColor?: string;
}

export interface IntegrationIssueResultData {
  /** Integration provider — drives icon and accent color */
  provider: 'linear' | 'notion' | 'slack' | 'google_drive' | 'gmail' | 'dropbox';
  /** Header text, e.g. "Issue created in Linear" */
  headerText: string;
  /** Issue identifier, e.g. "ENG-247" */
  identifier: string;
  /** Issue title */
  title: string;
  /** Tags for status, team, labels, etc. */
  tags?: IntegrationIssueTag[];
  /** URL to open in the external provider */
  externalUrl?: string;
  /** Internal issue ID for edit/update operations */
  issueId?: string;
  /** Callback for the "Edit issue" action */
  onEdit?: () => void;
}

export interface ChatPanelMessage extends ChatMessage {
  chips?: ActionChip[];
  /** When present, render a transcription card instead of a plain bubble */
  transcription?: TranscriptionData;
  /** When present, render an image result card instead of a plain bubble */
  imageResult?: ImageResultData;
  /** When present, render a structured analysis result card */
  analysisResult?: AnalysisResultData;
  /** When present, render a merge result card */
  mergeResult?: MergeResultData;
  /** When present, render a split result card */
  splitResult?: SplitResultData;
  /** When present, render an OCR result card */
  ocrResult?: OcrResultData;
  /** When present, render a QR code result card */
  qrResult?: QrResultData;
  /** When present, render a watermark result card */
  watermarkResult?: WatermarkResultData;
  /** When present, render a PDF utility result card */
  pdfUtilityResult?: PdfUtilityResultData;
  /** When present, render an integration issue card (Linear, Notion, etc.) */
  integrationIssueResult?: IntegrationIssueResultData;
  /** When present, render a generic tool result card (password protect, rotate, repair) */
  toolResult?: ToolResultCardData;
  /** When present, render a citation card */
  citationResult?: CitationResultData;
}

export interface AttachedFile {
  name: string;
  type?: string;
}

export interface TranscriptionMoment {
  time: string;
  text: string;
}

export interface TranscriptionData {
  /** e.g. "18.4s" */
  processingTime: string;
  /** e.g. ".txt" */
  downloadExt?: string;
  onDownload?: () => void;
  /** e.g. "Key moments" */
  heading?: string;
  moments: TranscriptionMoment[];
}

export interface ImageDownloadOption {
  format: string;
  /** When true, uses accent styling (orange bg/border) */
  primary?: boolean;
  onDownload?: () => void;
}

export interface ImageResultData {
  /** e.g. "2.1s" */
  processingTime: string;
  /** e.g. "Background removed" */
  actionLabel: string;
  /** Preview image URL */
  previewUrl?: string;
  /** Download options, e.g. [{format:'PNG', primary:true}, {format:'WebP'}] */
  downloads: ImageDownloadOption[];
  /** e.g. "headshot_bg_removed.png · 1.8 MB" */
  caption: string;
}

/** Rate limit info for the free plan limit card (Paper 15b) */
export interface RateLimitInfo {
  /** Used count, e.g. 20 */
  used: number;
  /** Max count, e.g. 20 */
  limit: number;
  /** Reset date label, e.g. "May 1, 2026" */
  resetsLabel: string;
  /** Called when "Upgrade to Pro" is clicked */
  onUpgrade: () => void;
  /** Called when "View usage details" is clicked */
  onViewUsage: () => void;
}

interface ChatPanelProps {
  title?: string;
  subtitle?: string;
  /** Use the compact 40px header with × close button instead of the 54px header */
  compactHeader?: boolean;
  /** Shown to the left of the close control when `compactHeader` (e.g. Apply my signature). */
  compactHeaderTrailing?: import('react').ReactNode;
  messages: ChatPanelMessage[];
  onSend: (message: string) => void;
  onStop?: () => void;
  onChipClick?: (label: string) => void;
  attachedFile?: AttachedFile | null;
  attachedFiles?: AttachedFile[];
  onRemoveFile?: (index?: number) => void;
  onClose?: () => void;
  disabled?: boolean;
  /** When true, use wider 400px layout (e.g. when tool panel is expanded) */
  wide?: boolean;
  /** When non-null, shows spinner on the last Corner message */
  processing?: boolean;
  /** Chain-of-Thought state for step-by-step progress visualization */
  cotState?: CoTState;
  /** Inline CoT confirmation callbacks */
  onConfirmStep?: (stepIndex: number) => void;
  onCancelStep?: (stepIndex: number) => void;
  onClarifyAnswer?: (stepIndex: number, answer: string) => void;
  onClarifyCancel?: (stepIndex: number) => void;
  onEditBatchChange?: (batch: EditBatch) => void;
  onEditBatchVersionReady?: (batch: EditBatch, htmlRevision?: HtmlEditApplyResult) => void;
  onEditBatchRecoveryVersionReady?: (result: DocEditRecoveryResult) => void;
  documentVersions?: DocumentVersionSummary[];
  onSelectDocumentVersion?: (version: DocumentVersionSummary) => void;
  editArtifactFormat?: 'source' | 'pdf';
  onToggleActivityPhase?: (phaseId: string) => void;
  onToggleActivityStep?: (phaseId: string, stepId: string) => void;
  /** App context for enriching CoT error messages */
  cotErrorRenderContext?: import('./cot/CoTContainer').CoTErrorRenderContext;
  /** V2 query run state (when active, renders QueryRunPanel instead of V1 CoT) */
  queryRun?: UseQueryRunStateResult;
  /** Shown on the V2 placeholder before the first snapshot (document + user prompt). */
  queryRunBootContext?: string;
  /** Called when user clicks an export destination in the ExportBar */
  onExportClick?: (destination: ExportDestination, messageId?: string) => void;
  /** True when more older messages can be loaded (Paper 2NE-0 — message pagination) */
  hasMoreMessages?: boolean;
  /** True while older messages are being fetched */
  loadingEarlierMessages?: boolean;
  /** Called when user clicks the "Load earlier messages" button */
  onLoadEarlierMessages?: () => void;
  /** When set, shows a rate limit card in the chat and disables input (Paper 15b) */
  rateLimitInfo?: RateLimitInfo | null;
  /** Called when user clicks "Save as workflow" on an analysis result */
  onSaveAsWorkflow?: (messageId: string) => void;
  /**
   * Called when user clicks "Save as Workflow" on a contract-send confirmation
   * message. The handler receives the message id; it is expected to look up
   * the message's `contractSendCapture` metadata to drive
   * `saveContractSendAsWorkflow()`.
   */
  onSaveContractSendAsWorkflow?: (messageId: string) => void;
  /** Called when user clicks "Edit issue" on an integration issue card */
  onEditLinearIssue?: (issueId: string) => void;
  /** Active right-panel tab for collaboration */
  activeTab?: 'analysis' | 'comments';
  /** Called when user switches between Comments and Analysis tabs */
  onTabChange?: (tab: 'analysis' | 'comments') => void;
  /** Number of comment threads for badge */
  commentCount?: number;
  /** Comment pins for the Comments tab thread list */
  commentPins?: import('@corner/shared').CommentPinData[];
  /** Currently active/selected comment pin ID */
  activeCommentPinId?: string | null;
  /** Called when a comment thread card is clicked */
  onCommentPinClick?: (pinId: string) => void;
  /** Override the panel width (for resizable layout) */
  widthOverride?: number;
  /** Render inside the mobile bottom sheet instead of the desktop side panel. */
  mobileSheet?: boolean;
  /** Auth token getter for authenticated file downloads */
  getToken?: () => string | null;
  /** Shown above the chat text field (e.g. workflow destination after e-sign completes). */
  composerAccessory?: import('react').ReactNode;
  /** Guided document-signing controls rendered inside the chat stream. */
  signingGuide?: import('react').ReactNode;
  /** Selected text snippet from the document viewer. */
  selectedSnippet?: SelectedSnippetChip | null;
  /** Remove the selected text snippet from the composer. */
  onRemoveSnippet?: () => void;
}

// ─── Corner logo ────────────────────────────────

function CornerLogo() {
  return (
    <svg width="14" height="14" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="corner-chat-grad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
          <stop stopColor="#D46A3A" />
          <stop offset="1" stopColor="#8B3A10" />
        </linearGradient>
      </defs>
      <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#corner-chat-grad)" strokeWidth="40" strokeLinecap="round" />
    </svg>
  );
}

// ─── Sub-components ─────────────────────────────

function CornerAvatar() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <CornerLogo />
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.04em',
          color: APP.textPrimary,
          fontFamily: APP.fontFamily,
          lineHeight: '14px',
          flexShrink: 0,
        }}
      >
        CORNER
      </span>
    </div>
  );
}

const SPINNER_KEYFRAMES = `@keyframes cornerSpinnerRotate { to { transform: rotate(360deg); } }`;

function LoadingSpinner() {
  return (
    <div style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }}>
      <style>{SPINNER_KEYFRAMES}</style>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ animation: 'cornerSpinnerRotate 0.9s linear infinite' }}>
        <circle cx="7" cy="7" r="5.5" stroke={APP.border} strokeWidth="1.5" />
        <path d="M7 1.5A5.5 5.5 0 0 1 12.5 7" stroke="#D46A3A" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

function WorkflowRunThreadStrip({
  summary,
  getToken,
}: {
  summary: WorkflowThreadSummary;
  getToken?: () => string | null;
}) {
  const token = getToken?.() ?? null;
  const downloadHref =
    summary.primaryDownloadFileId && token
      ? appendTokenToUrl(`/api/file/${summary.primaryDownloadFileId}`, () => token)
      : null;
  const label = summary.primaryDownloadFileName ?? 'Download';

  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: 10,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgCardBody,
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontFamily: APP.fontFamily,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: APP.textMuted,
          lineHeight: '16px',
        }}
      >
        {summary.steps.slice(0, 6).map((s, i) => (
          <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {i > 0 && <span aria-hidden="true" style={{ color: APP.border }}>→</span>}
            <span
              style={{
                fontWeight: s.done ? 600 : 400,
                color: s.done ? '#1B7A4B' : APP.textMuted,
                maxWidth: 140,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={s.detail ?? s.label}
            >
              {s.label}
            </span>
          </span>
        ))}
      </div>
      {downloadHref && (
        <a
          href={downloadHref}
          download={summary.primaryDownloadFileName ?? true}
          style={{
            alignSelf: 'flex-start',
            fontSize: 12,
            fontWeight: 600,
            color: APP.bgExportChipActive,
            textDecoration: 'none',
          }}
        >
          {label}
        </a>
      )}
    </div>
  );
}

function MessageBubble({ message, onChipClick, onExportClick, onSaveAsWorkflow, onSaveContractSendAsWorkflow, onEditLinearIssue, isProcessing, getToken }: { message: ChatPanelMessage; onChipClick?: (label: string) => void; onExportClick?: (destination: ExportDestination, messageId?: string) => void; onSaveAsWorkflow?: (messageId: string) => void; onSaveContractSendAsWorkflow?: (messageId: string) => void; onEditLinearIssue?: (issueId: string) => void; isProcessing?: boolean; getToken?: () => string | null }) {
  const isUser = message.role === 'user';
  const hasWorkflowThread = !isUser && message.workflowThreadSummary;
  const hasTranscription = !isUser && message.transcription;
  const hasImageResult = !isUser && message.imageResult;
  const hasAnalysisResult = !isUser && message.analysisResult;
  const hasEsignSummary = !isUser && message.esignSummary;
  const hasMergeResult = !isUser && message.mergeResult;
  const hasSplitResult = !isUser && message.splitResult;
  const hasOcrResult = !isUser && message.ocrResult;
  const hasQrResult = !isUser && message.qrResult;
  const hasWatermarkResult = !isUser && message.watermarkResult;
  const hasPdfUtilityResult = !isUser && message.pdfUtilityResult;
  const hasIntegrationIssueResult = !isUser && message.integrationIssueResult;
  const hasToolResult = !isUser && message.toolResult;
  const hasCitationResult = !isUser && message.citationResult;

  // Determine which card to render
  const renderCard = hasWorkflowThread || hasTranscription || hasImageResult || hasAnalysisResult || hasEsignSummary || hasMergeResult || hasSplitResult || hasOcrResult || hasQrResult || hasWatermarkResult || hasPdfUtilityResult || hasIntegrationIssueResult || hasToolResult || hasCitationResult;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        gap: 8,
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      {/* Avatar row — only for corner messages */}
      {!isUser && <CornerAvatar />}

      {renderCard ? (
        hasWorkflowThread
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 480 }}>
              <WorkflowRunThreadStrip summary={message.workflowThreadSummary!} getToken={getToken} />
              {message.content?.trim() && (
                <div className="corner-md" style={{ fontSize: 14, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '20px' }}>
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              )}
            </div>
          )
          : hasTranscription
          ? <TranscriptionCard data={message.transcription!} />
          : hasImageResult
          ? <ImageResultCard data={message.imageResult!} />
          : hasEsignSummary
          ? <ESignCard data={message.esignSummary!} getToken={getToken} />
          : hasMergeResult
          ? <MergeResultCard data={message.mergeResult!} />
          : hasSplitResult
          ? <SplitResultCard data={message.splitResult!} />
          : hasOcrResult
          ? <OcrResultCard data={message.ocrResult!} />
          : hasQrResult
          ? <QrResultCard data={message.qrResult!} />
          : hasWatermarkResult
          ? <WatermarkResultCard data={message.watermarkResult!} />
          : hasPdfUtilityResult
          ? <PdfUtilityResultCard data={message.pdfUtilityResult!} />
          : hasIntegrationIssueResult
          ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {message.content && (
                <div className="corner-md" style={{ fontSize: 14, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '20px' }}>
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              )}
              <IntegrationIssueCard data={{
                ...message.integrationIssueResult!,
                onEdit: message.integrationIssueResult!.issueId && message.integrationIssueResult!.provider === 'linear' && onEditLinearIssue
                  ? () => onEditLinearIssue(message.integrationIssueResult!.issueId!)
                  : message.integrationIssueResult!.onEdit,
              }} />
            </div>
          )
          : hasToolResult
          ? <ToolResultCard data={message.toolResult!} />
          : hasCitationResult
          ? <CitationCard data={message.citationResult!} />
          : hasAnalysisResult
          ? <AnalysisCard data={message.analysisResult!} onChipClick={onChipClick} onExportClick={(dest) => onExportClick?.(dest, message.id)} onSaveAsWorkflow={onSaveAsWorkflow ? () => onSaveAsWorkflow(message.id) : undefined} />
          : null
      ) : isProcessing && !isUser ? (
        /* Processing message — spinner + text, no bubble background */
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
          <LoadingSpinner />
          <span
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: APP.textCardBody,
              fontFamily: APP.fontFamily,
              lineHeight: '20px',
              whiteSpace: 'pre-wrap',
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
            }}
          >
            {message.content}
          </span>
        </div>
      ) : (
        /* Regular text bubble */
        <div
          style={{
            maxWidth: isUser ? 220 : '100%',
            borderRadius: isUser
              ? '8px 8px 0px 8px'
              : '0px 8px 8px 8px',
            paddingBlock: 10,
            paddingInline: 12,
            backgroundColor: isUser ? APP.bgUserBubble : APP.bgCornerBubble,
            overflow: 'hidden',
            wordBreak: 'break-word' as const,
          }}
        >
          {isUser ? (
            <span
              style={{
                fontSize: 13,
                fontWeight: 400,
                color: APP.textUserBubble,
                fontFamily: APP.fontFamily,
                lineHeight: '21px',
                whiteSpace: 'pre-wrap',
                display: 'block',
              }}
            >
              {message.content}
            </span>
          ) : (
            <div className="corner-md" style={{ fontSize: 13, color: APP.textPrimary, fontFamily: APP.fontFamily, lineHeight: '21px' }}>
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {/*
        Contract velocity hook: when an assistant message confirms a successful
        send-for-signature, surface a tiny "Save as Workflow" pill so the user
        can collapse this 90-second flow into a one-click workflow.
      */}
      {!isUser && message.contractSendCapture && onSaveContractSendAsWorkflow && (
        <button
          type="button"
          onClick={() => onSaveContractSendAsWorkflow(message.id)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            alignSelf: 'flex-start',
            borderRadius: 999,
            paddingBlock: 5,
            paddingInline: 10,
            border: `1px solid ${APP.border}`,
            backgroundColor: APP.bgInitialChip,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 11,
            fontWeight: 500,
            color: APP.textPrimary,
            lineHeight: '14px',
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Save as Workflow
        </button>
      )}
    </div>
  );
}

function ChipRow({ chips, onChipClick }: { chips: ActionChip[]; onChipClick?: (label: string) => void }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {chips.map((chip) => (
        <button
          key={chip.label}
          type="button"
          onClick={() => onChipClick?.(chip.sendAs ?? chip.label)}
          style={{
            borderRadius: 999,
            paddingBlock: chip.variant === 'initial' ? 5 : 4,
            paddingInline: chip.variant === 'initial' ? 11 : 10,
            backgroundColor: chip.variant === 'initial' ? APP.bgInitialChip : APP.bgFollowUpChip,
            border: `1px solid ${APP.border}`,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 11,
            fontWeight: 400,
            color: APP.textPrimary,
            lineHeight: '14px',
          }}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

function TranscriptionCard({ data }: { data: TranscriptionData }) {
  return (
    <div
      style={{
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 8,
        overflow: 'clip',
        border: `1px solid ${APP.borderTranscriptCard}`,
      }}
    >
      {/* Header: meta + download */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBlock: 8,
          paddingInline: 12,
          backgroundColor: APP.bgTranscriptCardHeader,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Audio icon */}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={APP.textTranscribedMeta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M9 18V5l12-2v13" />
            <circle cx="6" cy="18" r="3" fill="none" />
            <circle cx="18" cy="16" r="3" fill="none" />
          </svg>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: APP.textTranscribedMeta,
              fontFamily: APP.fontFamily,
              lineHeight: '14px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Transcribed in {data.processingTime}
          </span>
        </div>

        {data.onDownload && (
          <button
            type="button"
            onClick={data.onDownload}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 5,
              paddingBlock: 3,
              paddingInline: 8,
              gap: 4,
              backgroundColor: APP.bgDownloadBtn,
              border: `1px solid ${APP.borderDownloadBtn}`,
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 10,
              fontWeight: 400,
              color: APP.textDownloadBtn,
              lineHeight: '12px',
              flexShrink: 0,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M7 2v7M4 6.5L7 10l3-3.5M2 11h10" stroke={APP.textDownloadBtn} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download {data.downloadExt ?? '.txt'}
          </button>
        )}
      </div>

      {/* Body: heading + moments */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlock: 10,
          paddingInline: 12,
          gap: 8,
          backgroundColor: APP.bgTranscriptCardBody,
        }}
      >
        {data.heading && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: APP.textPrimary,
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
            }}
          >
            {data.heading}
          </span>
        )}

        {data.moments.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.moments.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 8 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: '#D97645',
                    fontFamily: APP.fontFamily,
                    lineHeight: '14px',
                    paddingTop: 1,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    width: 'fit-content',
                  }}
                >
                  {m.time}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 400,
                    color: APP.textPrimary,
                    fontFamily: APP.fontFamily,
                    lineHeight: '17px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.text}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const CHAT_CHECKERBOARD_BG = `
  linear-gradient(45deg, #D4CCC3 25%, transparent 25%),
  linear-gradient(-45deg, #D4CCC3 25%, transparent 25%),
  linear-gradient(45deg, transparent 75%, #D4CCC3 75%),
  linear-gradient(-45deg, transparent 75%, #D4CCC3 75%)
`.trim();

function ImageResultCard({ data }: { data: ImageResultData }) {
  return (
    <div
      style={{
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 8,
        overflow: 'clip',
        border: `1px solid ${APP.borderTranscriptCard}`,
      }}
    >
      {/* Header: icon + meta */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBlock: 8,
          paddingInline: 12,
          backgroundColor: APP.bgTranscriptCardHeader,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Sparkle/image icon */}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={APP.textTranscribedMeta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: APP.textTranscribedMeta,
              fontFamily: APP.fontFamily,
              lineHeight: '14px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {data.actionLabel} in {data.processingTime}
          </span>
        </div>
      </div>

      {/* Body: preview + downloads + caption */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlock: 10,
          paddingInline: 12,
          gap: 10,
          backgroundColor: APP.bgTranscriptCardBody,
        }}
      >
        {/* Image preview */}
        <div
          style={{
            width: '100%',
            height: 80,
            borderRadius: 6,
            overflow: 'clip',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: APP.border,
            backgroundImage: CHAT_CHECKERBOARD_BG,
            backgroundSize: '12px 12px',
            backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
          }}
        >
          {data.previewUrl && (
            <img
              src={data.previewUrl}
              alt="Result preview"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          )}
        </div>

        {/* Download buttons */}
        <div style={{ display: 'flex', gap: 6 }}>
          {data.downloads.map((dl) => (
            <button
              key={dl.format}
              type="button"
              onClick={dl.onDownload}
              style={{
                flexGrow: 1,
                flexShrink: 1,
                flexBasis: '0%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                paddingBlock: 6,
                paddingInline: 10,
                gap: 4,
                backgroundColor: dl.primary ? APP.bgImageResultAccentBtn : APP.bgImageResultBtn,
                border: `1px solid ${dl.primary ? APP.borderImageResultAccentBtn : APP.borderImageResultBtn}`,
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
                fontSize: 11,
                fontWeight: dl.primary ? 500 : 400,
                color: dl.primary ? APP.textImageResultAccent : APP.textImageResultBtn,
                lineHeight: '14px',
              }}
            >
              {/* Download arrow */}
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                <path
                  d="M7 2v7M4 6.5L7 10l3-3.5M2 11h10"
                  stroke={dl.primary ? APP.textImageResultAccent : APP.textImageResultBtn}
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {dl.format}
            </button>
          ))}
        </div>

        {/* Caption */}
        <span
          style={{
            fontSize: 11,
            fontWeight: 400,
            color: APP.textImageResultCaption,
            fontFamily: APP.fontFamily,
            lineHeight: '14px',
          }}
        >
          {data.caption}
        </span>
      </div>
    </div>
  );
}

// ─── E-Sign summary card ────────────────────────

function ESignCard({ data, onDownload, getToken }: { data: EsignChatSummary; onDownload?: () => void; getToken?: () => string | null }) {
  const handleDownload = onDownload ?? (data.downloadUrl && data.fileName && getToken
    ? () => { authedDownload(data.downloadUrl!, data.fileName!, getToken).catch(() => {}); }
    : undefined);

  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Field rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {data.rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: APP.textCardBody,
                fontFamily: APP.fontFamily,
                lineHeight: '16px',
              }}
            >
              {row.labelLine}
            </span>
            {row.status === 'signed' ? (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1B7A4B',
                  fontFamily: APP.fontFamily,
                  lineHeight: '14px',
                }}
              >
                ✓ Signed
              </span>
            ) : (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  color: APP.textCardMeta,
                  fontFamily: APP.fontFamily,
                  lineHeight: '14px',
                }}
              >
                Awaiting
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Download button */}
      {handleDownload && (
        <button
          type="button"
          onClick={handleDownload}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '8px 16px',
            backgroundColor: '#D97645',
            border: 'none',
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: '#FFFFFF',
            lineHeight: '16px',
          }}
        >
          {data.downloadCtaLabel}
        </button>
      )}

      {/* File meta */}
      {data.fileMetaLine && (
        <span
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: APP.textCardMeta,
            fontFamily: APP.fontFamily,
            lineHeight: '16px',
          }}
        >
          {data.fileMetaLine}
        </span>
      )}
    </SuccessCardShell>
  );
}

// ─── Shared success card shell (15g pattern) ────

function SuccessCardShell({ headerTitle, children }: { headerTitle: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${APP.borderCard}`,
        maxWidth: '100%',
      }}
    >
      {/* Success header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          backgroundColor: '#1B7A4B0F',
          borderBottom: `1px solid ${APP.borderCard}`,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="8" cy="8" r="7" stroke="#1B7A4B" strokeWidth="1.5" fill="none" />
          <polyline points="5,8 7,10.5 11,5.5" stroke="#1B7A4B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#1B7A4B', fontFamily: APP.fontFamily, lineHeight: '16px' }}>
          {headerTitle}
        </span>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: 16, gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Merge result card ──────────────────────────

function MergeResultCard({ data }: { data: MergeResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* File list with orange dots */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {data.files.map((file, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#D97645',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: APP.textCardDetail,
                fontFamily: APP.fontFamily,
                lineHeight: '16px',
              }}
            >
              {file.name} · {file.pageCount} pp
            </span>
          </div>
        ))}
      </div>

      {/* Download button */}
      <button
        type="button"
        onClick={data.onDownload}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          padding: '8px 16px',
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 500,
          color: '#FFFFFF',
          lineHeight: '16px',
        }}
      >
        Download merged PDF
      </button>

      {/* File meta */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textCardMeta,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {data.fileMetaLine}
      </span>
    </SuccessCardShell>
  );
}

// ─── Split result card ─────────────────────────

function SplitResultCard({ data }: { data: SplitResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* File list with individual download icons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {data.files.map((file, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 6,
              padding: '6px 8px',
              backgroundColor: APP.bgCodeBlock,
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 400,
                color: APP.textCardBody,
                fontFamily: APP.fontFamily,
                lineHeight: '16px',
              }}
            >
              {file.name}
            </span>
            <button
              type="button"
              onClick={() => {
                if (!file.downloadUrl) return;
                const getToken = data.getToken ?? (() => null);
                void downloadUrlWithAuthIfNeeded(file.downloadUrl, file.name, getToken).catch(() => {});
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                flexShrink: 0,
                color: APP.textCardMeta,
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: file.downloadUrl ? 'pointer' : 'default',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M6.5 1v7M6.5 8L3.5 5M6.5 8L9.5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M2 10h9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      {/* Download all as ZIP button */}
      <button
        type="button"
        onClick={data.onDownloadZip}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          padding: '8px 16px',
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 500,
          color: '#FFFFFF',
          lineHeight: '16px',
        }}
      >
        Download all as ZIP
      </button>
    </SuccessCardShell>
  );
}

// ─── OCR result card (Paper 5ZD) ────────────────

function OcrResultCard({ data }: { data: OcrResultData }) {
  const [copied, setCopied] = useState(false);

  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Text preview */}
      <div
        style={{
          borderRadius: 6,
          padding: '10px 12px',
          backgroundColor: APP.bgCodeBlock,
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 400,
            color: APP.textCardBody,
            fontFamily: APP.fontFamily,
            lineHeight: '18px',
            whiteSpace: 'pre-wrap',
            display: 'block',
          }}
        >
          {data.textPreview}
        </span>
      </div>

      {/* Action buttons: Download .txt + Copy */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={data.onDownload}
          style={{
            flex: '1 1 0%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '8px 16px',
            backgroundColor: '#D97645',
            border: 'none',
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: '#FFFFFF',
            lineHeight: '16px',
          }}
        >
          Download .txt
        </button>
        <button
          type="button"
          onClick={() => {
            data.onCopy();
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: `1px solid ${APP.borderCard}`,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: APP.textCardDetail,
            lineHeight: '16px',
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Footer */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textCardMeta,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {data.footerLine}
      </span>
    </SuccessCardShell>
  );
}

// ─── PDF Utility result card (Paper 6I7-0) ───────

function PdfUtilityResultCard({ data }: { data: PdfUtilityResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Detail row (optional) */}
      {data.detailLabel && data.detailValue && (
        <div style={{ display: 'flex', gap: 6 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 400,
              color: APP.textCardMeta,
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
            }}
          >
            {data.detailLabel}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: APP.textCardBody,
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
            }}
          >
            {data.detailValue}
          </span>
        </div>
      )}

      {/* Download button */}
      <button
        type="button"
        onClick={data.onDownload}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          padding: '8px 16px',
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 500,
          color: '#FFFFFF',
          lineHeight: '16px',
        }}
      >
        {data.downloadLabel}
      </button>

      {/* Footer: filename + size */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textCardMeta,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {data.footerLine}
      </span>
    </SuccessCardShell>
  );
}

// ─── Watermark result card (Paper 6BD-0) ─────────

function WatermarkResultCard({ data }: { data: WatermarkResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Watermark preview strip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 6,
          padding: '8px 10px',
          backgroundColor: APP.bgCodeBlock,
          overflow: 'hidden',
          height: 32,
        }}
      >
        <span
          style={{
            fontSize: 13,
            fontWeight: 900,
            color: '#3D322940',
            fontFamily: APP.fontFamily,
            letterSpacing: '0.04em',
            lineHeight: '16px',
            transform: 'rotate(-20deg)',
            whiteSpace: 'nowrap',
          }}
        >
          {data.watermarkText}
        </span>
      </div>

      {/* Download button */}
      <button
        type="button"
        onClick={data.onDownload}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          padding: '8px 16px',
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 500,
          color: '#FFFFFF',
          lineHeight: '16px',
        }}
      >
        Download watermarked PDF
      </button>

      {/* Footer: filename + size */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textCardMeta,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {data.footerLine}
      </span>
    </SuccessCardShell>
  );
}

// ─── Generic tool result card (Paper 15g — UO8-1) ─

const TOOL_RESULT_ICONS: Record<string, React.ReactNode> = {
  lock: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <rect x="3" y="9" width="14" height="9" rx="2" stroke={APP.textCardDetail} strokeWidth="1.5" fill="none" />
      <path d="M6 9V6C6 3.8 7.8 2 10 2C12.2 2 14 3.8 14 6V9" stroke={APP.textCardDetail} strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  ),
  rotate: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <path d="M14 2V6H18" stroke={APP.textCardDetail} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M14 6C12.5 3.5 9.5 2 6.5 2.5C3.5 3 1.5 5.5 1.5 8.5C1.5 11.5 3.5 14 6 15" stroke={APP.textCardDetail} strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  ),
  repair: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
      <path d="M10 18C14.4 18 18 14.4 18 10C18 5.6 14.4 2 10 2C5.6 2 2 5.6 2 10C2 14.4 5.6 18 10 18Z" stroke={APP.textCardDetail} strokeWidth="1.5" fill="none" />
      <path d="M7 10L9 12L13 8" stroke={APP.textCardDetail} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  ),
};

function ToolResultCard({ data }: { data: ToolResultCardData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Icon + description */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {TOOL_RESULT_ICONS[data.icon]}
        <span style={{ fontSize: 14, fontWeight: 400, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '18px' }}>
          {data.description}
        </span>
      </div>

      {/* File meta (optional) */}
      {data.fileMeta && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 400, color: APP.textCardMeta, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
            {data.fileMeta.pages}
          </span>
          <div style={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: APP.dotSeparator, flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 400, color: APP.textCardMeta, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
            {data.fileMeta.size}
          </span>
        </div>
      )}

      {/* Detail list box (optional — e.g. repair issues) */}
      {data.details && data.details.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', borderRadius: 6, padding: '10px 12px', gap: 4, backgroundColor: APP.bgCodeBlock }}>
          {data.details.map((item, i) => (
            <span key={i} style={{ fontSize: 12, fontWeight: 400, color: APP.textCardDetail, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
              {item}
            </span>
          ))}
        </div>
      )}

      {/* Download button */}
      <button
        type="button"
        onClick={data.onDownload}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          padding: '8px 16px',
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 13,
          fontWeight: 500,
          color: '#FFFFFF',
          lineHeight: '16px',
        }}
      >
        Download
      </button>
    </SuccessCardShell>
  );
}

// ─── Citation card (Paper 15g — UO8-1) ──────────

function CitationCard({ data }: { data: CitationResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
        {/* Citation text box */}
        <div style={{ display: 'flex', flexDirection: 'column', borderRadius: 6, padding: 12, gap: 4, backgroundColor: APP.bgCodeBlock }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: APP.textCardMeta,
              fontFamily: APP.fontFamily,
              lineHeight: '14px',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.05em',
            }}
          >
            {data.formatLabel}
          </span>
          <span style={{ fontSize: 13, fontWeight: 400, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '18px', whiteSpace: 'pre-wrap' }}>
            {data.citationText}
          </span>
        </div>

        {/* Actions row: Copy button + format toggles */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={data.onCopy}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              padding: '8px 16px',
              backgroundColor: '#D97645',
              border: 'none',
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 13,
              fontWeight: 500,
              color: '#FFFFFF',
              lineHeight: '16px',
            }}
          >
            Copy to clipboard
          </button>
          {data.formats.filter(f => !f.active).map((f, i) => (
            <button
              key={f.label}
              type="button"
              onClick={f.onSelect}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
                fontSize: 13,
                fontWeight: i === 0 ? 500 : 400,
                color: i === 0 ? '#D97645' : APP.textCardMeta,
                lineHeight: '16px',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
    </SuccessCardShell>
  );
}

// ─── Integration issue card (Paper 15f — UE9-1) ─

/** Provider accent colors + icons */
const PROVIDER_THEME: Record<string, { accent: string; bg: string; tagBg: string }> = {
  linear:       { accent: '#5E6AD2', bg: '#5E6AD2',   tagBg: '#5E6AD21A' },
  notion:       { accent: '#000000', bg: '#000000',   tagBg: '#0000001A' },
  slack:        { accent: '#E01E5A', bg: '#4A154B',   tagBg: '#4A154B1A' },
  google_drive: { accent: '#1A73E8', bg: '#1A73E8',   tagBg: '#1A73E81A' },
  gmail:        { accent: '#EA4335', bg: '#EA4335',   tagBg: '#EA43351A' },
  dropbox:      { accent: '#0061FF', bg: '#0061FF',   tagBg: '#0061FF1A' },
};

function ProviderIcon({ provider }: { provider: string }) {
  const theme = PROVIDER_THEME[provider] ?? PROVIDER_THEME.linear;
  return (
    <div style={{ width: 20, height: 20, borderRadius: 4, backgroundColor: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {provider === 'linear' ? (
        <svg width="10" height="10" viewBox="0 0 100 100" fill="none">
          <path d="M1.225 61.523c-.2225-.9485.907-1.546 1.596-.857L39.334 97.178c.6889.689.091 1.819-.857 1.596C20.052 94.452 5.548 79.948 1.225 61.523Z" fill="#fff" />
          <path d="M.00189 46.889c-.1764.283.889.56.29.761L52.35 99.709c.2007.201.477.307.761.29 2.369-.1476 4.694-.46 6.962-.9259.764-.157 1.03-1.096.478-1.648L2.576 39.449c-.5519-.5519-1.491-.2863-1.648.478-.4659 2.269-.7783 4.593-.92588 6.962Z" fill="#fff" />
          <path d="M4.211 29.705c-.1665.374-.0817.811.208 1.1l64.776 64.776c.2894.289.726.374 1.1.208 1.786-.7956 3.517-1.693 5.186-2.684.552-.328.637-1.087.183-1.541L8.436 24.337c-.4541-.4541-1.213-.3689-1.541.183-.9913 1.668-1.888 3.399-2.684 5.186Z" fill="#fff" />
          <path d="M12.659 18.074c-.3701-.3701-.393-.9637-.0443-1.354C21.779 6.459 35.111 0 49.952 0 77.593 0 100 22.407 100 50.048c0 14.841-6.459 28.172-16.72 37.337-.3903.349-.984.326-1.354-.0443L12.659 18.074Z" fill="#fff" />
        </svg>
      ) : (
        /* Generic placeholder icon for other providers */
        <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
          <rect x="1" y="1" width="12" height="12" rx="2" stroke="#fff" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  );
}

function IntegrationIssueCard({ data }: { data: IntegrationIssueResultData }) {
  const theme = PROVIDER_THEME[data.provider] ?? PROVIDER_THEME.linear;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${APP.borderCard}`,
      }}
    >
      {/* Success header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          backgroundColor: '#1B7A4B0F',
          borderBottom: `1px solid ${APP.borderCard}`,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="8" cy="8" r="7" stroke="#1B7A4B" strokeWidth="1.5" fill="none" />
          <polyline points="5,8 7,10.5 11,5.5" stroke="#1B7A4B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#1B7A4B', fontFamily: APP.fontFamily, lineHeight: '16px' }}>
          {data.headerText}
        </span>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: 16, gap: 12, backgroundColor: APP.bgCardBody }}>
        {/* Provider icon + identifier + title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <ProviderIcon provider={data.provider} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, minWidth: 0 }}>
            <span style={{ fontSize: 12, fontWeight: 400, color: APP.textCardDetail, fontFamily: APP.fontFamily, lineHeight: '16px' }}>
              {data.identifier}
            </span>
            <span style={{ fontSize: 14, fontWeight: 500, color: APP.textCardBody, fontFamily: APP.fontFamily, lineHeight: '18px' }}>
              {data.title}
            </span>
          </div>
        </div>

        {/* Tags */}
        {data.tags && data.tags.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            {data.tags.map((tag, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 4,
                  padding: '3px 8px',
                  backgroundColor: tag.highlighted ? theme.tagBg : APP.bgCodeBlock,
                }}
              >
                {tag.dotColor && (
                  <div style={{ width: 6, height: 6, borderRadius: 3, backgroundColor: tag.dotColor, flexShrink: 0 }} />
                )}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    color: tag.highlighted ? theme.accent : APP.textCardDetail,
                    fontFamily: APP.fontFamily,
                    lineHeight: '16px',
                  }}
                >
                  {tag.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        {(data.externalUrl || data.onEdit) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4, borderTop: `1px solid ${APP.borderCard}` }}>
            {data.externalUrl && (
              <a
                href={data.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#D97645',
                  fontFamily: APP.fontFamily,
                  lineHeight: '16px',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                Open in {data.provider === 'linear' ? 'Linear' : data.provider === 'notion' ? 'Notion' : data.provider.charAt(0).toUpperCase() + data.provider.slice(1)}
              </a>
            )}
            {data.onEdit && (
              <button
                type="button"
                onClick={data.onEdit}
                style={{
                  borderRadius: 6,
                  padding: '6px 12px',
                  border: `1px solid ${APP.borderCard}`,
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 13,
                  fontWeight: 500,
                  color: APP.textCardBody,
                  lineHeight: '16px',
                }}
              >
                Edit issue
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── QR result card (Paper 64J-0) ───────────────

function QrResultCard({ data }: { data: QrResultData }) {
  return (
    <SuccessCardShell headerTitle={data.headerTitle}>
      {/* Subtitle */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textCardMeta,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {data.subtitle}
      </span>

      {/* Action buttons: Download PNG + SVG */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={data.onDownloadPrimary}
          style={{
            flex: '1 1 0%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '8px 16px',
            backgroundColor: '#D97645',
            border: 'none',
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: '#FFFFFF',
            lineHeight: '16px',
          }}
        >
          {data.primaryLabel}
        </button>
        <button
          type="button"
          onClick={data.onDownloadAlternate}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 8,
            padding: '8px 16px',
            backgroundColor: 'transparent',
            border: `1px solid ${APP.borderCard}`,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 13,
            fontWeight: 500,
            color: APP.textCardDetail,
            lineHeight: '16px',
          }}
        >
          {data.alternateLabel}
        </button>
      </div>
    </SuccessCardShell>
  );
}

// ─── Analysis result card ───────────────────────

function AnalysisCard({
  data,
  onChipClick,
  onExportClick,
  onSaveAsWorkflow,
}: {
  data: AnalysisResultData;
  onChipClick?: (label: string) => void;
  onExportClick?: (destination: ExportDestination) => void;
  onSaveAsWorkflow?: () => void;
}) {
  const [exportBarVisible, setExportBarVisible] = useState(false);
  const [activeExportDest, setActiveExportDest] = useState<ExportDestination | null>(null);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderTopLeftRadius: 3,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 10,
        overflow: 'clip',
        backgroundColor: APP.bgCardBody,
        border: `1px solid ${APP.borderCard}`,
      }}
    >
      {/* Content area: title + body + chips */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlock: 12,
          paddingInline: 14,
          gap: 8,
        }}
      >
        {/* Title */}
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: APP.textPrimary,
            lineHeight: '16px',
            fontFamily: APP.fontFamily,
          }}
        >
          {data.title}
        </span>

        {/* Body */}
        <div className="corner-md" style={{ fontSize: 13, color: APP.textCardBody, lineHeight: '19px', fontFamily: APP.fontFamily }}>
          <ReactMarkdown>{data.body}</ReactMarkdown>
        </div>

        {data.riskSummary && data.riskSummary.nudgeLevel !== 'silent' && (
          <RiskReviewEntryBanner
            variant="chatCard"
            nudgeLevel={data.riskSummary.nudgeLevel === 'reconsider' ? 'reconsider' : 'attention'}
            label={data.riskSummary.label}
            laneLabel={data.riskSummary.lane === 'real_estate' ? 'Real Estate' : 'Business / Founder'}
            jurisdictionLabel={data.riskSummary.jurisdiction}
            ctaLabel={data.riskSummary.nudgeLevel === 'reconsider' ? 'Review now' : 'Review'}
            onPrimary={() => onChipClick?.('Run clause review')}
          />
        )}

        {/* Action chips + Export summary trigger */}
        {(data.chips.length > 0 || data.exportable) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {data.chips.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => onChipClick?.(chip.sendAs ?? chip.label)}
                style={{
                  flexShrink: 0,
                  borderRadius: 20,
                  paddingBlock: 4,
                  paddingInline: 10,
                  border: `1px solid ${APP.border}`,
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 14,
                  fontWeight: 400,
                  color: APP.textPrimary,
                  lineHeight: '20px',
                }}
              >
                {chip.label}
              </button>
            ))}
            {data.exportable && (
              <button
                type="button"
                onClick={() => setExportBarVisible((v) => !v)}
                style={{
                  flexShrink: 0,
                  borderRadius: 20,
                  paddingBlock: 4,
                  paddingInline: 10,
                  border: `1px solid ${APP.border}`,
                  backgroundColor: exportBarVisible ? APP.bgExportBar : 'transparent',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 14,
                  fontWeight: 500,
                  color: APP.textPrimary,
                  lineHeight: '20px',
                }}
              >
                Export summary
              </button>
            )}
            {onSaveAsWorkflow && (
              <button
                type="button"
                onClick={onSaveAsWorkflow}
                style={{
                  flexShrink: 0,
                  borderRadius: 20,
                  paddingBlock: 4,
                  paddingInline: 10,
                  border: `1px solid ${APP.border}`,
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 14,
                  fontWeight: 400,
                  color: APP.textPrimary,
                  lineHeight: '20px',
                }}
              >
                Save as workflow
              </button>
            )}
          </div>
        )}
      </div>

      {/* EXPORT TO bar */}
      {exportBarVisible && (
        <div style={{ paddingBlock: 6, paddingInline: 14 }}>
          <ExportBar
            variant="compact"
            activeDestination={activeExportDest}
            onDestinationClick={(dest) => {
              setActiveExportDest(dest);
              onExportClick?.(dest);
            }}
            onClose={() => {
              setExportBarVisible(false);
              setActiveExportDest(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

function FileChipCompact({
  file,
  onRemove,
}: {
  file: AttachedFile;
  onRemove?: () => void;
}) {
  const fallbackType = file.name.split('.').pop() || 'file';
  const fileType = file.type || fallbackType;
  const badge = fileType.toUpperCase();
  const badgeBg = getFileChipBadgeColor(fileType);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        minWidth: 0,
        borderRadius: 6,
        paddingBlock: 4,
        paddingInline: 8,
        gap: 5,
        backgroundColor: APP.bgCardBody,
        border: `1px solid ${APP.border}`,
        boxSizing: 'border-box' as const,
      }}
    >
      {/* Type badge */}
      <span
        style={{
          flexShrink: 0,
          display: 'inline-block',
          borderRadius: 3,
          paddingBlock: 1,
          paddingInline: 4,
          backgroundColor: badgeBg,
          fontSize: 10,
          fontWeight: 700,
          color: '#FFFFFF',
          fontFamily: APP.fontFamily,
          lineHeight: '12px',
        }}
      >
        {badge}
      </span>

      {/* Filename — truncated */}
      <span
        style={{
          fontSize: 11,
          fontWeight: 400,
          color: APP.textPrimary,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {file.name}
      </span>

      {/* Dismiss */}
      <button
        type="button"
        onClick={onRemove}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          marginLeft: 'auto',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
          <path d="M2 2l6 6M8 2l-6 6" stroke={APP.textFileBadgeDismiss} strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

function FileChipGrid({
  files,
  onRemoveFile,
}: {
  files: AttachedFile[];
  onRemoveFile?: (index: number) => void;
}) {
  // 2 files: side-by-side row; 3+: vertical stack (matches Paper 5K1-0 design)
  if (files.length === 2) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        {files.map((file, idx) => (
          <FileChipCompact
            key={idx}
            file={file}
            onRemove={() => onRemoveFile?.(idx)}
          />
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {files.map((file, idx) => (
        <FileChipCompact
          key={idx}
          file={file}
          onRemove={() => onRemoveFile?.(idx)}
        />
      ))}
    </div>
  );
}

function QueueEmptyState({ fileCount }: { fileCount: number }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: '0%',
        padding: 24,
        gap: 10,
      }}
    >
      {/* File icon container */}
      <div
        style={{
          width: 36,
          height: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 10,
          backgroundColor: APP.bgQueueIcon,
          flexShrink: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP.cornerGradientStart} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </svg>
      </div>

      {/* Title */}
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: APP.textNewConversation,
          fontFamily: APP.fontFamily,
          lineHeight: '16px',
        }}
      >
        {fileCount} file{fileCount !== 1 ? 's' : ''} queued
      </span>

      {/* Helper text */}
      <span
        style={{
          fontSize: 12,
          fontWeight: 400,
          color: APP.textQueueBody,
          fontFamily: APP.fontFamily,
          lineHeight: '18px',
          textAlign: 'center',
          maxWidth: 200,
        }}
      >
        Ask anything across all your files, or type a command to process them.
      </span>
    </div>
  );
}

// ─── Rate Limit Card (Paper 15b) ────────────────

function RateLimitCard({ info }: { info: RateLimitInfo }) {
  const pct = info.limit > 0 ? Math.min((info.used / info.limit) * 100, 100) : 100;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 12,
        paddingBlock: 16,
        paddingInline: 16,
        gap: 12,
        backgroundColor: '#FFF9F0',
        border: '1.5px solid #E8B86A',
      }}
    >
      {/* Header: icon + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            backgroundColor: '#F5E6D0',
          }}
        >
          <svg width={16} height={16} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
            <path d="M8 2V10M8 13V13.01" stroke="#D97645" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="8" r="7" stroke="#D97645" strokeWidth="1.5" fill="none" />
          </svg>
        </div>
        <span style={{ fontSize: 15, fontWeight: 600, color: APP.textDark, lineHeight: '18px', fontFamily: APP.fontFamily }}>
          Free plan limit reached
        </span>
      </div>

      {/* Description */}
      <span style={{ fontSize: 13, fontWeight: 400, color: APP.textCardDetail, lineHeight: '19px', fontFamily: APP.fontFamily }}>
        You've used all {info.limit} AI conversations for this month. Resets {info.resetsLabel}.
      </span>

      {/* Usage meter */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 500, color: '#9B8E83', lineHeight: '14px', fontFamily: APP.fontFamily }}>
            AI Conversations
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#D97645', lineHeight: '14px', fontFamily: APP.fontFamily }}>
            {info.used} / {info.limit}
          </span>
        </div>
        <div style={{ display: 'flex', height: 4, borderRadius: 2, overflow: 'clip', backgroundColor: APP.borderCard, flexShrink: 0 }}>
          <div style={{ width: `${pct}%`, height: 4, borderRadius: 2, backgroundColor: '#D97645' }} />
        </div>
      </div>

      {/* Upgrade button */}
      <button
        type="button"
        onClick={info.onUpgrade}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          paddingBlock: 10,
          paddingInline: 10,
          backgroundColor: '#D97645',
          border: 'none',
          cursor: 'pointer',
          fontFamily: APP.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: '#FFFFFF',
          lineHeight: '18px',
        }}
      >
        Upgrade to Pro
      </button>

      {/* View usage details link */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={info.onViewUsage}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: APP.fontFamily,
            fontSize: 12,
            fontWeight: 400,
            color: APP.textCardDetail,
            lineHeight: '16px',
            textDecoration: 'underline',
            textDecorationThickness: '1px',
            textUnderlineOffset: '2px',
          }}
        >
          View usage details
        </button>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────

export default function ChatPanel({
  title = 'Analysis',
  subtitle,
  messages,
  onSend,
  onStop,
  onChipClick,
  attachedFile,
  attachedFiles: attachedFilesProp,
  onRemoveFile,
  compactHeader = false,
  compactHeaderTrailing,
  onClose,
  disabled = false,
  wide = false,
  processing = false,
  cotState,
  onConfirmStep,
  onCancelStep,
  onClarifyAnswer,
  onClarifyCancel,
  onEditBatchChange,
  onEditBatchVersionReady,
  onEditBatchRecoveryVersionReady,
  documentVersions,
  onSelectDocumentVersion,
  editArtifactFormat = 'source',
  onToggleActivityPhase,
  onToggleActivityStep,
  cotErrorRenderContext,
  queryRun,
  queryRunBootContext,
  onExportClick,
  hasMoreMessages = false,
  loadingEarlierMessages = false,
  onLoadEarlierMessages,
  rateLimitInfo,
  onSaveAsWorkflow,
  onSaveContractSendAsWorkflow,
  onEditLinearIssue,
  activeTab = 'analysis',
  onTabChange,
  commentCount = 0,
  commentPins,
  activeCommentPinId,
  onCommentPinClick,
  widthOverride,
  mobileSheet = false,
  getToken,
  composerAccessory,
  signingGuide,
  selectedSnippet,
  onRemoveSnippet,
}: ChatPanelProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  // Track previous-render state so we can distinguish a prepend (load earlier)
  // from an append (new reply) and preserve scroll position when older
  // messages are loaded — Paper 2NE-0.
  const prevFirstMsgIdRef = useRef<string | null>(null);
  const prevScrollHeightRef = useRef<number>(0);

  // Normalize: support both single and multi-file props
  const files: AttachedFile[] = attachedFilesProp ??
    (attachedFile ? [attachedFile] : []);

  const hasMessages = messages.filter((m) => m.role !== 'system').length > 0;
  const isNewConversation = !hasMessages && files.length > 0;

  // Auto-scroll on new messages, but preserve scroll position when older
  // messages are prepended via "Load earlier messages".
  useLayoutEffect(() => {
    const container = messagesScrollRef.current;
    const visible = messages.filter((m) => m.role !== 'system');
    const firstId = visible[0]?.id ?? null;
    // A true prepend keeps the previous first message somewhere in the new
    // list (now no longer at index 0). A conversation switch replaces the
    // list entirely, so the previous first id won't be present.
    const wasPrepended =
      prevFirstMsgIdRef.current !== null &&
      firstId !== null &&
      prevFirstMsgIdRef.current !== firstId &&
      visible.some((m) => m.id === prevFirstMsgIdRef.current);

    if (wasPrepended && container) {
      // Keep the user's current view stable: shift scrollTop by the height
      // of the newly inserted content above.
      const heightDelta = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop = container.scrollTop + heightDelta;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    prevFirstMsgIdRef.current = firstId;
    prevScrollHeightRef.current = container?.scrollHeight ?? 0;
  }, [messages]);

  // Auto-scroll when CoT steps change (new step becomes active)
  const cotStatus = cotState?.status;
  const cotStepsLength = cotState?.steps.length ?? 0;
  const cotActivitySignal = cotState?.activityPhases
    .map((phase) => {
      const last = phase.steps[phase.steps.length - 1];
      return `${phase.id}:${phase.steps.length}:${last?.id ?? ''}:${last?.kind === 'thinking' ? last.body.length : 0}`;
    })
    .join('|') ?? '';
  const queryRunActivitySignal = queryRun?.snapshot
    ? [
        queryRun.snapshot.status,
        queryRun.snapshot.finalAnswer?.length ?? 0,
        queryRun.streamingAnswer.length,
        queryRun.thinkingSegments.length,
        queryRun.thinkingSegments.at(-1)?.text.length ?? 0,
        queryRun.snapshot.tasks.map((task) => `${task.id}:${task.status}:${task.visibleSteps.length}:${task.result?.length ?? 0}:${task.error?.length ?? 0}`).join('|'),
      ].join(':')
    : queryRun
      ? [
          queryRun.connectionState,
          queryRun.streamingAnswer.length,
          queryRun.thinkingSegments.length,
          queryRun.thinkingSegments.at(-1)?.text.length ?? 0,
        ].join(':')
      : '';
  useEffect(() => {
    if (
      (cotStatus && cotStatus !== 'idle' && (cotStepsLength > 0 || cotActivitySignal)) ||
      queryRunActivitySignal
    ) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [cotStatus, cotStepsLength, cotActivitySignal, queryRunActivitySignal]);

  useEffect(() => {
    if (selectedSnippet?.text) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [selectedSnippet?.text]);

  const handleSubmit = () => {
    const trimmed = value.trim();
    if ((!trimmed && !selectedSnippet) || disabled) return;
    onSend(trimmed);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      style={{
        width: mobileSheet ? '100%' : (widthOverride ?? (wide ? 400 : 316)),
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: mobileSheet ? 1 : 0,
        overflow: 'hidden',
        backgroundColor: APP.bgChatPanel,
        borderLeft: 'none',
        transition: mobileSheet ? undefined : 'width 240ms ease',
      }}
    >
      {/* ── Header ─────────────────────────────── */}
      {isNewConversation || compactHeader ? (
        /* Compact header: title + optional trailing actions + close button, 40px */
        <div
          style={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            paddingInline: 16,
            borderBottom: `1px solid ${APP.border}`,
            gap: 10,
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: APP.textNewConversation,
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
              flexShrink: 0,
            }}
          >
            {isNewConversation ? 'New conversation' : title}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 1, minWidth: 0, justifyContent: 'flex-end' }}>
            {compactHeaderTrailing}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M3 3l8 8M11 3l-8 8" stroke={APP.iconDefault} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ) : onTabChange ? (
        /* Collaboration header: Comments / Analysis tabs */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            borderBottom: `1px solid ${APP.border}`,
          }}
        >
          {/* Tab row */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 0 }}>
            <button
              type="button"
              onClick={() => onTabChange('comments')}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                backgroundColor: activeTab === 'comments' ? '#D976451A' : 'transparent',
                fontFamily: APP.fontFamily,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M1.5 2h10A1 1 0 0112.5 3v5a1 1 0 01-1 1H5L1.5 11V3A1 1 0 012.5 2H1.5Z"
                  stroke={activeTab === 'comments' ? '#D97645' : APP.iconDefault}
                  strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: activeTab === 'comments' ? 500 : 400, color: activeTab === 'comments' ? '#D97645' : APP.textMuted }}>
                Comments
              </span>
              {commentCount > 0 && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#FFFFFF',
                  backgroundColor: activeTab === 'comments' ? '#D97645' : '#E05A33',
                  borderRadius: 10,
                  minWidth: 18,
                  height: 18,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 5px',
                  lineHeight: 1,
                  fontFamily: APP.fontFamily,
                }}>
                  {commentCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => onTabChange('analysis')}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                backgroundColor: activeTab === 'analysis' ? '#D976451A' : 'transparent',
                fontFamily: APP.fontFamily,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: activeTab === 'analysis' ? 500 : 400, color: activeTab === 'analysis' ? '#D97645' : APP.textMuted }}>
                Analysis
              </span>
            </button>
            <div style={{ flexGrow: 1 }} />
            {onClose && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={onClose}>
                <path d="M2 7h10" stroke={APP.iconDefault} strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            )}
          </div>
          {/* Subtitle */}
          {subtitle && (
            <div style={{ padding: '0 16px 10px' }}>
              <span style={{ fontSize: 11, fontWeight: 400, color: APP.textReviewingSub, textTransform: 'uppercase' as const, letterSpacing: '0.05em', fontFamily: APP.fontFamily }}>
                {subtitle}
              </span>
            </div>
          )}
        </div>
      ) : (
        /* Default Analysis header: title + dash + subtitle, 54px */
        <div
          style={{
            height: 54,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexShrink: 0,
            paddingInline: 14,
            gap: 2,
            borderBottom: `1px solid ${APP.border}`,
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: APP.textPrimary,
              fontFamily: APP.fontFamily,
              lineHeight: '16px',
            }}
          >
            {title}
          </span>
          {/* Decorative dash separator */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
            <path d="M2 7h10" stroke={APP.iconDefault} strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          {subtitle && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'uppercase' as const,
                color: APP.textReviewingSub,
                fontFamily: APP.fontFamily,
                lineHeight: '14px',
              }}
            >
              {subtitle}
            </span>
          )}
        </div>
      )}

      {activeTab !== 'comments' && signingGuide ? (
        <div
          style={{
            flexShrink: 0,
            padding: '12px 14px',
            borderBottom: `1px solid ${APP.border}`,
            backgroundColor: APP.bgChatPanel,
            maxHeight: mobileSheet ? '42dvh' : '46%',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {signingGuide}
        </div>
      ) : null}

      {/* ── Messages Area / Queue State ──────── */}
      {activeTab === 'comments' && commentPins ? (
        <CommentThreadList
          pins={commentPins}
          activePinId={activeCommentPinId}
          onPinClick={onCommentPinClick}
        />
      ) : isNewConversation ? (
        <QueueEmptyState fileCount={files.length} />
      ) : (
        <div
          ref={messagesScrollRef}
          style={{
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: '0%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            paddingBlock: 16,
            paddingInline: 16,
            overflowY: 'auto',
            overflowX: 'hidden',
            gap: 14,
          }}
        >
          {/* Load earlier messages trigger — Paper 2NE-0 */}
          {hasMoreMessages && (
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 10,
                paddingBottom: 6,
              }}
            >
              <button
                type="button"
                onClick={onLoadEarlierMessages}
                disabled={loadingEarlierMessages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 20,
                  padding: '6px 14px',
                  backgroundColor: APP.bgToolSearch,
                  border: `1px solid ${APP.border}`,
                  cursor: loadingEarlierMessages ? 'default' : 'pointer',
                  fontFamily: APP.fontFamily,
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                  color: APP.textMuted,
                  opacity: loadingEarlierMessages ? 0.6 : 1,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M6 9.5V2.5M3 5.5L6 2.5L9 5.5" stroke={APP.textMuted} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {loadingEarlierMessages ? 'Loading…' : 'Load earlier messages'}
              </button>
            </div>
          )}
          {(() => {
            const cotActive = cotState && cotState.status !== 'idle';
            // Anchor CoT at the assistant MessageBubble whenever we have a linked id.
            // File/tool results often omit CoT `answer` text (onCoTDone(undefined)) while
            // handleToolResult still sets answerMessageId — requiring both left the slot
            // empty and duplicated CoT + bubble (see useCoTState.onPlanning clear).
            const cotAnswerMsgId = cotState?.answerMessageId ?? null;

            // ── CoT dedup debug ──
            console.log('[CoT DEBUG] ChatPanel_render', {
              cotStatus: cotState?.status,
              cotHasAnswer: !!cotState?.answer,
              answerMessageId: cotState?.answerMessageId ?? null,
              cotActive,
              cotAnswerMsgId,
              totalMessages: messages.length,
              cornerMessages: messages.filter(m => m.role === 'corner').map(m => ({ id: m.id.slice(0, 8), toolName: (m as any).toolName, type: (m as any).type })),
            });

            // Hide system messages only. If a CoT answer is associated with a
            // persisted assistant message, render the CoT block at that message's
            // chronological slot so later user prompts do not jump above it.
            const visible = messages.filter((msg) => {
              if (msg.role === 'system') return false;
              return true;
            });

            console.log('[CoT DEBUG] visible_after_filter', {
              cotAnswerMsgId,
              visibleCount: visible.length,
              visibleCorner: visible.filter(m => m.role === 'corner').map(m => ({ id: m.id.slice(0, 8), toolName: (m as any).toolName })),
            });

            // Find the last Corner message index for spinner placement (only when CoT is NOT active)
            let lastCornerIdx = -1;
            if (processing && !cotActive) {
              for (let i = visible.length - 1; i >= 0; i--) {
                if (visible[i].role === 'corner') { lastCornerIdx = i; break; }
              }
            }
            const renderCotBlock = (): React.ReactNode => {
              const editBatchEntries = (cotState?.steps ?? [])
                .map((step, index) => ({ batch: step.editBatch, index }))
                .filter((entry): entry is { batch: EditBatch; index: number } => Boolean(entry.batch));
              const renderEditBatchCards = () => editBatchEntries.map(({ batch, index }) => (
                <CoTEditBatchCard
                  key={`edit-batch-${batch.id || index}`}
                  batch={batch}
                  getToken={getToken}
                  onBatchChange={onEditBatchChange}
                  onVersionReady={onEditBatchVersionReady}
                  onRecoveryVersionReady={onEditBatchRecoveryVersionReady}
                  documentVersions={documentVersions}
                  onSelectDocumentVersion={onSelectDocumentVersion}
                  artifactFormat={editArtifactFormat}
                />
              ));

              if (queryRun && !queryRun.snapshot) {
                console.log('[CoT DEBUG] rendering query run placeholder', { connectionState: queryRun.connectionState });
                return (
                  <>
                    <CoTContainer
                      key="query-run-placeholder"
                      status={queryRun.connectionState === 'error' ? 'error' : 'planning'}
                      steps={[]}
                      understanding={queryRunBootContext}
                      answer={queryRun.streamingAnswer ? { content: queryRun.streamingAnswer } : undefined}
                      activityPhases={queryRun.thinkingSegments.length > 0 ? [{
                        id: 'query-run-thinking',
                        collapsed: false,
                        steps: queryRun.thinkingSegments.map((segment, index) => ({
                          id: `thinking-${index}`,
                          kind: 'thinking' as const,
                          label: 'Thinking',
                          body: segment.text,
                          status: 'active' as const,
                          expanded: true,
                        })),
                      }] : undefined}
                      errorRenderContext={cotErrorRenderContext}
                      answerStreaming={!!queryRun.streamingAnswer}
                      onEditBatchChange={onEditBatchChange}
                      onEditBatchVersionReady={onEditBatchVersionReady}
                      getToken={getToken}
                      editArtifactFormat={editArtifactFormat}
                    />
                    {renderEditBatchCards()}
                  </>
                );
              }

              if (queryRun?.snapshot) {
                console.log('[CoT DEBUG] rendering QueryRunPanel');
                return (
                  <>
                    <QueryRunPanel
                      key="query-run-panel"
                      snapshot={queryRun.snapshot}
                      streamingAnswer={queryRun.streamingAnswer}
                      thinkingSegments={queryRun.thinkingSegments}
                      onRetryNode={queryRun.retryNode}
                      onSkipNode={queryRun.skipNode}
                      isNodeActionPending={queryRun.isNodeActionPending}
                      getNodeDenial={queryRun.getNodeDenial}
                      errorRenderContext={cotErrorRenderContext}
                      mergeEditBatchesFromSteps={cotState?.steps}
                      onEditBatchChange={onEditBatchChange}
                      onEditBatchVersionReady={onEditBatchVersionReady}
                      onEditBatchRecoveryVersionReady={onEditBatchRecoveryVersionReady}
                      documentVersions={documentVersions}
                      onSelectDocumentVersion={onSelectDocumentVersion}
                      getToken={getToken}
                      editArtifactFormat={editArtifactFormat}
                    />
                  </>
                );
              }

              const hasCotRenderableContent = Boolean(
                cotState &&
                (
                  cotState.steps.length > 0 ||
                  cotState.activityPhases.length > 0 ||
                  cotState.answer ||
                  cotState.status === 'planning' ||
                  cotState.status === 'working'
                )
              );

              if (cotActive && hasCotRenderableContent) {
                const cotStatus = cotState.status === 'done' ? 'done'
                  : cotState.status === 'error' ? 'error'
                  : cotState.status === 'planning' ? 'planning'
                  : 'working';
                console.log('[CoT DEBUG] rendering CoTContainer', { cotStatus, hasAnswer: !!cotState.answer, answerMessageId: cotState.answerMessageId });
                return (
                  <CoTContainer
                    key="cot-container"
                    status={cotStatus}
                    steps={cotState.steps}
                    answer={cotState.answer}
                    understanding={cotState.understanding}
                    activityPhases={cotState.activityPhases}
                    errorRenderContext={cotErrorRenderContext}
                    partialCompletion={cotState.partialCompletion}
                    onConfirmStep={onConfirmStep}
                    onCancelStep={onCancelStep}
                    onClarifyAnswer={onClarifyAnswer}
                    onClarifyCancel={onClarifyCancel}
                    onEditBatchChange={onEditBatchChange}
                    onEditBatchVersionReady={onEditBatchVersionReady}
                    onEditBatchRecoveryVersionReady={onEditBatchRecoveryVersionReady}
                    documentVersions={documentVersions}
                    onSelectDocumentVersion={onSelectDocumentVersion}
                    editArtifactFormat={editArtifactFormat}
                    getToken={getToken}
                    onToggleActivityPhase={onToggleActivityPhase}
                    onToggleActivityStep={onToggleActivityStep}
                  />
                );
              }

              if (editBatchEntries.length > 0) {
                return <>{renderEditBatchCards()}</>;
              }

              console.log('[CoT DEBUG] NO CoTContainer rendered (cotActive=', cotActive, 'steps=', cotState?.steps?.length ?? 0, ')');
              return null;
            };

            let renderedCotAtMessageSlot = false;
            const elements = visible.flatMap((msg, idx) => {
              if (cotAnswerMsgId && msg.id === cotAnswerMsgId) {
                const node = renderCotBlock();
                const slotEls: React.ReactNode[] = [];
                if (node) slotEls.push(node);
                // Tool/file completions often clear CoT `answer` (onCoTDone(undefined)) while
                // the assistant row still carries result cards — keep that bubble under the timeline.
                if (
                  !cotState?.answer
                  && (msg.result || (typeof msg.content === 'string' && msg.content.trim().length > 0))
                ) {
                  slotEls.push(
                    <MessageBubble
                      key={`${msg.id}-under-cot`}
                      message={msg}
                      onChipClick={onChipClick}
                      onExportClick={onExportClick}
                      onSaveAsWorkflow={onSaveAsWorkflow}
                      onSaveContractSendAsWorkflow={onSaveContractSendAsWorkflow}
                      onEditLinearIssue={onEditLinearIssue}
                      isProcessing={idx === lastCornerIdx}
                      getToken={getToken}
                    />,
                  );
                  if (msg.chips && msg.chips.length > 0) {
                    slotEls.push(
                      <ChipRow key={`${msg.id}-chips-cot`} chips={msg.chips} onChipClick={onChipClick} />,
                    );
                  }
                }
                renderedCotAtMessageSlot = slotEls.length > 0;
                return slotEls;
              }
              const els: React.ReactNode[] = [
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  onChipClick={onChipClick}
                  onExportClick={onExportClick}
                  onSaveAsWorkflow={onSaveAsWorkflow}
                  onSaveContractSendAsWorkflow={onSaveContractSendAsWorkflow}
                  onEditLinearIssue={onEditLinearIssue}
                  isProcessing={idx === lastCornerIdx}
                  getToken={getToken}
                />,
              ];
              if (msg.chips && msg.chips.length > 0) {
                els.push(
                  <ChipRow key={`${msg.id}-chips`} chips={msg.chips} onChipClick={onChipClick} />
                );
              }
              return els;
            });

            if (!renderedCotAtMessageSlot) {
              const node = renderCotBlock();
              if (node) elements.push(node);
            }

            return elements;
          })()}
          {rateLimitInfo && <RateLimitCard info={rateLimitInfo} />}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* ── Input Area ─────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          paddingTop: 10,
          paddingRight: 14,
          paddingBottom: 14,
          paddingLeft: 14,
          gap: 8,
          flexShrink: 0,
          borderTop: `1px solid ${APP.border}`,
          backgroundColor: isNewConversation ? APP.bgChatInputActive : undefined,
        }}
      >
        {/* Context chips — selected snippets and files stay attached to this chat composer. */}
        {selectedSnippet || files.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            {selectedSnippet ? (
              <SnippetChip snippet={selectedSnippet} onRemove={onRemoveSnippet} disabled={disabled} />
            ) : null}
            {files.length > 1 ? (
              <FileChipGrid
                files={files}
                onRemoveFile={(idx) => onRemoveFile?.(idx)}
              />
            ) : files.length === 1 ? (
              <FileChipCompact file={files[0]} onRemove={() => onRemoveFile?.(0)} />
            ) : null}
          </div>
        ) : null}

        {composerAccessory ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            {composerAccessory}
          </div>
        ) : null}

        {/* Chat input bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: isNewConversation ? 8 : 10,
            paddingBlock: 9,
            paddingInline: 12,
            gap: 8,
            backgroundColor: APP.bgChatInput,
            border: isNewConversation
              ? `1.5px solid ${APP.borderChatInputAccent}`
              : `1px solid ${APP.border}`,
            boxSizing: 'border-box' as const,
            minWidth: 0,
          }}
        >
          {/* Paperclip */}
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={rateLimitInfo ? '#8B7D6B' : APP.iconDefault} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>

          {/* Text input */}
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={rateLimitInfo ? 'Upgrade to continue using AI features' : selectedSnippet ? 'Ask about this selection...' : isNewConversation ? `Summarize all ${files.length} files...` : 'type a command...'}
            disabled={disabled || !!rateLimitInfo}
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
              minWidth: 0,
              width: '100%',
              fontSize: 13,
              fontFamily: APP.fontFamily,
              color: rateLimitInfo ? '#8B7D6B' : APP.textPrimary,
              lineHeight: '16px',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              padding: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'clip',
            }}
          />

          {/* Send / Stop button */}
          {disabled && onStop && !rateLimitInfo ? (
            <button
              type="button"
              onClick={onStop}
              aria-label="Stop processing"
              title="Stop processing"
              style={{
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                backgroundColor: '#D94545',
                border: 'none',
                cursor: 'pointer',
                flexShrink: 0,
                padding: 0,
              }}
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
                <rect width="10" height="10" rx="1.5" fill="#FFFFFF" />
              </svg>
            </button>
          ) : (
            <button
              type="button"
              onClick={rateLimitInfo ? rateLimitInfo.onUpgrade : handleSubmit}
              disabled={!rateLimitInfo && (disabled || (!value.trim() && !selectedSnippet))}
              style={{
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                backgroundColor: rateLimitInfo ? '#C4622A' : APP.bgChatSendBtn,
                border: 'none',
                cursor: rateLimitInfo ? 'pointer' : disabled || (!value.trim() && !selectedSnippet) ? 'default' : 'pointer',
                flexShrink: 0,
                opacity: rateLimitInfo ? 1 : disabled || (!value.trim() && !selectedSnippet) ? 0.5 : 1,
                padding: 0,
              }}
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                <path d="M2 6.5h9M8 3.5l3 3-3 3" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
